import { useEffect } from 'react'
import { GuestLayout } from '@/layouts/GuestLayout'
import { trackEvent } from '@/lib/trackEngagement'
import type { GuestEvent, GuestProperty } from '@/lib/types'

interface Props {
  share_token: string
  property: GuestProperty
  event: GuestEvent
}

function hostInitials(name: string | null): string {
  if (!name) return '?'
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: '1 1 30%', minWidth: 88,
        padding: '12px 14px',
        background: 'var(--g-surface, #fff)',
        borderRadius: 12, border: '1px solid var(--g-line)',
      }}
    >
      <div className="g-mono" style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{value}</div>
    </div>
  )
}

export default function EventDetail({ share_token, property, event }: Props) {
  useEffect(() => { if (share_token) trackEvent(share_token, 'event_view', { event_id: event.id }) }, [])
  const initials = hostInitials(property.host_name)
  const brandSoft = property.brand_color + '40'

  const startDate = new Date(event.starts_at).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  })
  const startTime = new Date(event.starts_at).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  })

  return (
    <GuestLayout brandColor={property.brand_color}>
      <div style={{ background: 'var(--g-bg, #FBF7F1)', paddingBottom: 40 }}>

        {/* Hero */}
        <div style={{ position: 'relative', height: 280 }}>
          <div className="g-poi-stripe" style={{ position: 'absolute', inset: 0 }} />
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, var(--g-bg, #FBF7F1) 100%)',
            }}
          />
          <a
            href={`/g/${share_token}`}
            aria-label="Back to guide"
            className="g-tap"
            style={{
              position: 'absolute', top: 'calc(56px + env(safe-area-inset-top))', left: 16,
              width: 40, height: 40, borderRadius: 999,
              background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1F1A14" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </a>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px', marginTop: -24, position: 'relative' }}>

          {/* EVENT label */}
          <div
            className="g-mono"
            style={{
              fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: property.brand_color, marginBottom: 12, fontWeight: 600,
            }}
          >
            EVENT
          </div>

          {/* Title */}
          <h1
            className="g-serif"
            style={{ fontSize: 38, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--ink-900)', marginBottom: 18 }}
          >
            {event.name}
          </h1>

          {/* Info blocks */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 22, flexWrap: 'wrap' }}>
            <InfoBlock label="Date" value={startDate} />
            <InfoBlock label="Time" value={startTime} />
          </div>

          {/* Host note */}
          {event.host_note && (
            <div style={{ background: brandSoft, borderRadius: 16, padding: 18, marginBottom: 22 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <div
                  style={{
                    width: 28, height: 28, borderRadius: 999,
                    background: property.brand_color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
                <div className="g-mono" style={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>
                  {property.host_name ? `Note from ${property.host_name}` : 'Host note'}
                </div>
              </div>
              <p className="g-serif" style={{ fontSize: 19, lineHeight: 1.35, color: 'var(--ink-900)', fontStyle: 'italic', margin: 0 }}>
                "{event.host_note}"
              </p>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--ink-700)', marginBottom: 22 }}>
              {event.description}
            </p>
          )}

          {/* External link */}
          {event.external_url && (
            <a
              href={event.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="g-tap"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: 18, borderRadius: 14,
                background: 'var(--ink-900, #1F1A14)', color: 'var(--sand-50, #FBF7F1)',
                fontSize: 15, fontWeight: 500, textDecoration: 'none',
              }}
            >
              <span>More info</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6M10 14L21 3"/>
              </svg>
            </a>
          )}
        </div>
      </div>
    </GuestLayout>
  )
}
